import React, {useCallback} from 'react';
import styled from 'styled-components';
import {makeStyles} from '@material-ui/core/styles';
import Icon from '@mdi/react';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import {mdiTablet, mdiCreditCardOutline, mdiPrinterPos} from '@mdi/js';
import Register from 'features/POSRegisters/interfaces/Register';
import {hasAssociation, hasDeviceInformation} from 'features/POSRegisters/functions/alertTypes';

export enum RegisterTabs {
  IPAD_AIR = 'ipad-air',
  IPAD_MINI = 'ipad-mini',
  RECEIPT_PRINTER = 'receipt-printer',
  PAYMENT_TERMINAL = 'payment-terminal',
}

interface SummaryTabsProps {
  selectedTab?: string;
  handleTabChange: (tab: RegisterTabs) => void;
  register: Register;
  expanded: boolean;
}

//#region Constants
export const tabsDictionary: Record<string, RegisterTabs> = {
  IPAD_AIR: RegisterTabs.IPAD_AIR,
  IPAD_MINI: RegisterTabs.IPAD_MINI,
  RECEIPT_PRINTER: RegisterTabs.RECEIPT_PRINTER,
  PAYMENT_TERMINAL: RegisterTabs.PAYMENT_TERMINAL,
};
//#endRegion Constants

//#region styles
const useStyles = makeStyles(() => ({
  tooltip: {
    background: '#FFFFFF',
    color: '#89959D',
    borderRadius: 0,
    border: '1px solid #89959D',
  },
}));

const StyledTabContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  padding: 0px 0px 0px 0px;
  background: transparent;
  .MuiButtonBase-root.MuiButton-text {
    min-width: 0;
  }
`;

const StyledTab = styled(Button)`
  display: flex;
  padding: 4px 8px;
  color: ${props => props.theme.grey};
  text-decoration: none;
  font-family: 'Trade Gothic Bold Condensed';
  font-weight: 700;
  font-size: 16px;
  text-transform: none;
  border-bottom: 4px solid transparent;
  border-radius: 0;
  transition: 250ms;

  .MuiButton-label {
    margin-bottom: 5px;
    gap: 5px;
  }

  &.MuiButton-root {
    line-height: 17px;
  }

  &:hover {
    text-decoration: none;
    color: ${props => props.theme.text};
    border-bottom: 4px solid ${props => props.theme.text};
  }

  &.active {
    color: ${props => props.theme.text};
    border-bottom: 4px solid ${props => props.theme.text};

    &.minor {
      border-bottom: 4px solid ${props => props.theme.yellow};
    }

    &.major {
      border-bottom: 4px solid ${props => props.theme.orange};
    }

    &.critical {
      border-bottom: 4px solid ${props => props.theme.red};
    }
  }

  &.disabled,
  &.Mui-disabled {
    cursor: default;
    color: ${props => props.theme.white}1F;
  }
`;

const StyledIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  height: 24px;
  width: 24px;
`;

const StyledAlertsNotification = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
`;
//#end region styles

function SummaryTabs({register, selectedTab, handleTabChange, expanded}: SummaryTabsProps) {
  const classes = useStyles();
  const {ipad, ipadMini, receiptPrinter, barcodeScanner, cashDrawer, paymentTerminal} = register;

  const handleClick = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement>, tab: RegisterTabs) => {
      evt.preventDefault();
      if (!!selectedTab && !!expanded) evt.stopPropagation();
      handleTabChange(tab);
    },
    [expanded, selectedTab, handleTabChange]
  );

  return (
    <StyledTabContainer>
      <StyledTab
        className={`
          ${'ipad-air' === selectedTab && 'active'}
          ${getPriorityAlert([ipad])}
        `}
        onClick={evt => handleClick(evt, RegisterTabs.IPAD_AIR)}
        disabled={!hasDeviceInformation([ipad])}
      >
        <Tooltip
          classes={{tooltip: classes.tooltip}}
          title={'ipad-air' !== selectedTab ? 'iPad Air' : ''}
          aria-label="iPad Air"
          placement="top"
        >
          <StyledIconContainer>
            <Icon path={mdiTablet} size={1} />
            {hasAssociation(ipad?.scanResults) && (
              <StyledAlertsNotification>
                <SeverityBadge severity={getPriorityAlert([ipad])} size={0.75} />
              </StyledAlertsNotification>
            )}
          </StyledIconContainer>
        </Tooltip>
        {'ipad-air' === selectedTab && 'iPad Air'}
      </StyledTab>
      <StyledTab
        className={`
          ${'ipad-mini' === selectedTab && 'active'}
          ${getPriorityAlert([ipadMini])}
        `}
        onClick={evt => handleClick(evt, RegisterTabs.IPAD_MINI)}
        disabled={!hasDeviceInformation([ipadMini])}
      >
        <Tooltip
          classes={{tooltip: classes.tooltip}}
          title={'ipad-mini' !== selectedTab ? 'iPad Mini (CFD)' : ''}
          aria-label="iPad Mini (CFD)"
          placement="top"
        >
          <StyledIconContainer>
            <Icon path={mdiTablet} size={0.75} />
            {hasAssociation(ipadMini?.scanResults) && (
              <StyledAlertsNotification>
                <SeverityBadge severity={getPriorityAlert([ipadMini])} size={0.75} />
              </StyledAlertsNotification>
            )}
          </StyledIconContainer>
        </Tooltip>
        {'ipad-mini' === selectedTab && 'iPad Mini (CFD)'}
      </StyledTab>
      <StyledTab
        className={`
          ${'receipt-printer' === selectedTab && 'active'}
          ${getPriorityAlert([receiptPrinter, barcodeScanner, cashDrawer])}
        `}
        onClick={evt => handleClick(evt, RegisterTabs.RECEIPT_PRINTER)}
        disabled={!hasDeviceInformation([receiptPrinter, barcodeScanner, cashDrawer])}
      >
        <Tooltip
          classes={{tooltip: classes.tooltip}}
          title={'receipt-printer' !== selectedTab ? 'Receipt Printer' : ''}
          aria-label="Receipt Printer"
          placement="top"
        >
          <StyledIconContainer>
            <Icon path={mdiPrinterPos} size={1} />
            {hasAssociation(receiptPrinter?.scanResults) && (
              <StyledAlertsNotification>
                <SeverityBadge
                  severity={getPriorityAlert([receiptPrinter, barcodeScanner, cashDrawer])}
                  size={0.75}
                />
              </StyledAlertsNotification>
            )}
          </StyledIconContainer>
        </Tooltip>
        {'receipt-printer' === selectedTab && 'Receipt Printer'}
      </StyledTab>
      <StyledTab
        className={`
          ${'payment-terminal' === selectedTab && 'active'}
          ${getPriorityAlert([paymentTerminal])}}`}
        onClick={evt => handleClick(evt, RegisterTabs.PAYMENT_TERMINAL)}
        disabled={!hasDeviceInformation([paymentTerminal])}
      >
        <Tooltip
          classes={{tooltip: classes.tooltip}}
          title={'payment-terminal' !== selectedTab ? 'Payment Terminal' : ''}
          aria-label="Payment Terminal"
          placement="top"
        >
          <StyledIconContainer>
            <Icon path={mdiCreditCardOutline} size={1} />
            {hasAssociation(paymentTerminal?.scanResults) && (
              <StyledAlertsNotification>
                <SeverityBadge severity={getPriorityAlert([paymentTerminal])} size={0.75} />
              </StyledAlertsNotification>
            )}
          </StyledIconContainer>
        </Tooltip>
        {'payment-terminal' === selectedTab && 'Payment Terminal'}
      </StyledTab>
    </StyledTabContainer>
  );
}

export default SummaryTabs;
